import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Save,
  Trash2,
  Printer,
  Layers,
  ScrollText,
  CircleDot,
  Sliders,
  FileDown,
  Moon,
  Sun,
  ShieldCheck,
  Building2,
} from "lucide-react";
import IntakeForm, { STEPS } from "./components/forms/IntakeForm";
import AuditTrailDrawer from "./components/AuditTrailDrawer";
import DealTemplateLoader from "./components/DealTemplateLoader";
import { Link } from "react-router-dom";
import {
  Doc0SubjectNote,
  Doc0DiscountDraft,
  Doc1MasterNote,
  Doc2Pledge,
  Doc3UCC,
  Doc4Notice,
  Doc5Resolution,
  Doc6PurchaseAssignment,
  Doc6AAllonge,
  Doc6BBillOfSale,
  Doc6CNoticeToMaker,
  Doc6DClosingCert,
  Doc6EIncumbency,
  DocT01CertificateOfTrust,
  DocT02TrueSale,
  DocT03KYCMemo,
  DocT04TrustResolution,
  DocT05Custody,
  DocT06TrueSaleOpinion,
  DocD01DealerTermSheet,
  Doc6FProceedsDirection,
  Doc6GDisbursementAck,
} from "./components/documents/Documents";
import { defaultDeal, fmtCurrency } from "./lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SPV_TRUST_DOCS = [
  // Path 0 — SPV Trust Setup. Legal-execution order:
  // authority → entity → compliance → operative transfer → opinion → custody → commercial.
  { id: "doct04", label: "T-04 · Trust Resolution", component: DocT04TrustResolution },
  { id: "doct01", label: "T-01 · Certificate of Trust", component: DocT01CertificateOfTrust },
  { id: "doct03", label: "T-03 · KYC / AML Memo", component: DocT03KYCMemo },
  { id: "doct02", label: "T-02 · True Sale Transfer", component: DocT02TrueSale },
  { id: "doct06", label: "T-06 · True-Sale Opinion", component: DocT06TrueSaleOpinion },
  { id: "doct05", label: "T-05 · Custody Agreement", component: DocT05Custody },
  { id: "docd01", label: "D-01 · Dealer Term Sheet", component: DocD01DealerTermSheet },
];

const FINANCING_DOCS = [
  // Path 2 — Collateralized Loan (UCC Art. 9 secured lending). Execution order:
  // authority → operative → perfection → notice → collateral exhibit.
  { id: "doc5", label: "05 · Corporate Resolution", component: Doc5Resolution },
  { id: "doc1", label: "01 · Master Note", component: Doc1MasterNote },
  { id: "doc2", label: "02 · Pledge Agreement", component: Doc2Pledge },
  { id: "doc3", label: "03 · UCC-1 Statement", component: Doc3UCC },
  { id: "doc4", label: "04 · Notice of Pledge", component: Doc4Notice },
  { id: "doc0", label: "00 · Subject Note (Exhibit A)", component: Doc0SubjectNote },
];

const SALE_DOCS = [
  // Path 1 — Outright Note Sale via Discount Draft (UCC Art. 3 negotiation).
  // Authority → reps & warranties → operative → endorsement → bill of sale →
  // notice → proceeds → ack → exhibits.
  { id: "doc6e", label: "06-E · Incumbency Certificate", component: Doc6EIncumbency },
  { id: "doc6d", label: "06-D · Closing Certificate", component: Doc6DClosingCert },
  { id: "doc6", label: "06 · Purchase & Assignment", component: Doc6PurchaseAssignment },
  { id: "doc6a", label: "06-A · Allonge", component: Doc6AAllonge },
  { id: "doc6b", label: "06-B · Bill of Sale", component: Doc6BBillOfSale },
  { id: "doc6c", label: "06-C · Notice to Buyer", component: Doc6CNoticeToMaker },
  { id: "doc6f", label: "06-F · Proceeds Direction", component: Doc6FProceedsDirection },
  { id: "doc6g", label: "06-G · Disbursement Receipt", component: Doc6GDisbursementAck },
  { id: "doc0", label: "00 · Subject Note (Exhibit A)", component: Doc0SubjectNote },
  { id: "doc0a", label: "00-A · Private Negotiable Instrument (Exhibit B)", component: Doc0DiscountDraft },
  { id: "docd01", label: "D-01 · Dealer Term Sheet", component: DocD01DealerTermSheet },
];

const PATH_META = {
  spv_trust: {
    label: "SPV Trust Setup",
    subtitle: "UTC § 1013 · Bankruptcy-Remote Trust Formation",
    blurb: "Forms the bankruptcy-remote Special Purpose Vehicle (Treasury House Trust) that will acquire, hold, and liquidate negotiable instruments. Establishes EIN, KYC/AML compliance, and trustee signature authority before any sale or hypothecation.",
    counterparty: (d) => `${d.originatorName} (Originator) → ${d.trustName} (SPV Trust, EIN ${d.trustEIN})`,
  },
  financing: {
    label: "Collateralized Loan",
    subtitle: "UCC Art. 9 · Secured Lending",
    blurb: "Borrower retains ownership of the Subject Note and pledges it as collateral to the Lender. No outright transfer to a third-party buyer.",
    counterparty: (d) => `${d.lenderName} → secured by Subject Note pledged by ${d.borrowerName}`,
  },
  discount_sale: {
    label: "Outright Note Sale",
    subtitle: "UCC Art. 3 · Negotiation & Discount",
    blurb: "Seller permanently transfers 100% of the Subject Note to the Buyer for a discounted cash purchase price. No loan obligation remains.",
    counterparty: (d) => `${d.borrowerName} → ${d.buyerName} (Without Recourse · via ${d.acceptanceBankName})`,
  },
};

const SensitivitySlider = ({ label, min, max, step, value, display, onChange, testid }) => (
  <div data-testid={`${testid}-wrap`}>
    <div className="flex items-center justify-between mb-1">
      <label className="field-label" style={{ marginBottom: 0 }}>{label}</label>
      <span className="font-mono-fin text-[11px] text-[#0B4636] font-semibold">{display(Number(value))}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{ width: "100%", accentColor: "#0B4636" }}
      data-testid={testid}
    />
  </div>
);

const calcLocal = (d) => {
  const coupon = d.faceValue * (d.couponRate / 100) * (d.termDays / 365);
  const maturity = d.faceValue + coupon;
  const discount = maturity - d.purchasePrice;
  const discountPct = (discount / maturity) * 100;
  const holdingYield = (discount / d.purchasePrice) * 100;
  const annualYield = holdingYield * (365 / d.termDays);
  const loanInterest = d.loanPrincipal * (d.loanRate / 100) * (d.termDays / 365);
  const loanPayoff = d.loanPrincipal + loanInterest;
  const spread = maturity - loanPayoff;
  return { coupon, maturity, discount, discountPct, holdingYield, annualYield, loanInterest, loanPayoff, spread };
};

function App() {
  const [deal, setDeal] = useState(defaultDeal);
  const [mode, setMode] = useState("spv_trust"); // SPV foundation first
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditIntent, setAuditIntent] = useState(null);
  const [auditFocusTx, setAuditFocusTx] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const deliverTx = params.get("deliver");
    if (deliverTx) {
      setAuditFocusTx(deliverTx);
      setAuditIntent("payload");
      setAuditOpen(true);
      params.delete("deliver");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);
  const [stepIdx, setStepIdx] = useState(0);
  const [activeDoc, setActiveDoc] = useState("doct01");
  const [drafts, setDrafts] = useState([]);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [savingState, setSavingState] = useState("idle");
  const [printMode, setPrintMode] = useState(null); // 'single' | 'all' | null
  const [showSensitivity, setShowSensitivity] = useState(false);
  const [benchmark, setBenchmark] = useState(null);
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return window.localStorage.getItem("th-theme") || "dark";
  });

  // Apply theme to <body> + persist
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("dark-mode", theme === "dark");
    try { window.localStorage.setItem("th-theme", theme); } catch { /* localStorage unavailable */ }
  }, [theme]);

  // Poll the Treasury Fiscal Data benchmark every 5 min.
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/treasury/benchmark`);
        if (active) setBenchmark(r.data);
      } catch (e) { /* ignore */ }
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const setField = (name, value) =>
    setDeal((prev) => {
      const next = { ...prev, [name]: value };
      // Auto-mirror Trust Name -> Treasury House wordmark while in sync.
      // Stops mirroring as soon as the user manually edits treasuryHouseName.
      if (name === "trustName" && prev.treasuryHouseName === prev.trustName) {
        next.treasuryHouseName = value;
      }
      return next;
    });

  // When the user switches modes, detach the active draft so a save in
  // the new mode creates a fresh draft instead of overwriting the other
  // path's saved work.
  const switchMode = (next) => {
    setMode((prev) => {
      if (prev !== next) setActiveDraftId(null);
      return next;
    });
  };

  const docsList = mode === "spv_trust" ? SPV_TRUST_DOCS : mode === "financing" ? FINANCING_DOCS : SALE_DOCS;

  useEffect(() => {
    // Ensure activeDoc is valid for current mode
    if (!docsList.find((x) => x.id === activeDoc)) {
      setActiveDoc(docsList[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Fetch drafts on mount
  useEffect(() => {
    axios.get(`${API}/drafts`).then((r) => setDrafts(r.data || [])).catch(() => {});
  }, []);

  const stats = useMemo(() => calcLocal(deal), [deal]);

  const allDocsForPrint = docsList;

  const ActiveDoc = docsList.find((x) => x.id === activeDoc)?.component || DocT01CertificateOfTrust;

  // Clear print mode on browser afterprint event (works whether the
  // user actually saves the PDF or cancels the dialog).
  useEffect(() => {
    const handler = () => setPrintMode(null);
    window.addEventListener("afterprint", handler);
    return () => window.removeEventListener("afterprint", handler);
  }, []);

  const printDocument = (which = "single") => {
    setPrintMode(which);
    // Two RAFs ensure the print-root has been committed to the DOM
    // before window.print() snapshots it.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
        // Fallback in case afterprint doesn't fire on this browser
        setTimeout(() => setPrintMode((m) => (m === which ? null : m)), 500);
      });
    });
  };

  const saveDraft = async () => {
    setSavingState("saving");
    try {
      if (activeDraftId) {
        const r = await axios.put(`${API}/drafts/${activeDraftId}`, {
          title: deal.borrowerName + " — " + PATH_META[mode].label,
          mode,
          data: deal,
        });
        setDrafts((prev) => prev.map((x) => (x.id === activeDraftId ? r.data : x)));
      } else {
        const r = await axios.post(`${API}/drafts`, {
          title: deal.borrowerName + " — " + PATH_META[mode].label,
          mode,
          data: deal,
        });
        setDrafts((prev) => [r.data, ...prev]);
        setActiveDraftId(r.data.id);
      }
      setSavingState("saved");
      setTimeout(() => setSavingState("idle"), 1500);
    } catch (e) {
      setSavingState("error");
    }
  };

  const exportDocx = async () => {
    try {
      const r = await axios.post(
        `${API}/export/docx`,
        { path: mode, deal },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      const safe = (deal.trustName || deal.borrowerName || "deal").replace(/\s+/g, "_");
      a.download = `${safe}_${mode}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ }
  };

  const loadDraft = (d) => {
    setDeal({ ...defaultDeal, ...d.data });
    setMode(d.mode || "financing");
    setActiveDraftId(d.id);
  };

  const deleteDraft = async (id) => {
    await axios.delete(`${API}/drafts/${id}`);
    setDrafts((prev) => prev.filter((x) => x.id !== id));
    if (id === activeDraftId) setActiveDraftId(null);
  };

  const newDeal = () => {
    setDeal(defaultDeal);
    setActiveDraftId(null);
    setStepIdx(0);
  };

  const currentStep = STEPS[stepIdx];

  return (
    <div className="app-shell">
      {/* ============ TOP BAR ============ */}
      <header className="no-print sticky top-0 z-30 border-b border-[#E5E5DF] bg-white/95 backdrop-blur">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-sm bg-[#0B4636] flex items-center justify-center">
                <ScrollText size={15} color="#fff" strokeWidth={2.2} />
              </div>
              <div className="leading-tight">
                <div className="font-serif-display text-[19px] font-semibold tracking-tight">SEUTRADE FINANCIAL SERVICES</div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-[#5C5C5C] -mt-0.5">Note Liquidity Workbench</div>
              </div>
            </div>
            <div className="hidden md:block w-px h-7 bg-[#E5E5DF]" />
            <nav className="hidden md:flex items-center gap-1 text-[12px] text-[#5C5C5C]">
              <span className="dot" /> <span className="ml-1">Live draft</span>
              <span className="mx-3 text-[#D1D1C7]">/</span>
              <span>{deal.borrowerName}</span>
              <span className="mx-3 text-[#D1D1C7]">/</span>
              <span className="font-mono-fin">{fmtCurrency(deal.loanPrincipal, { cents: false })} facility</span>
              {benchmark?.avg_interest_rate && (
                <>
                  <span className="mx-3 text-[#D1D1C7]">/</span>
                  <span
                    className="font-mono-fin text-[#0B4636] cursor-pointer hover:underline"
                    title={`U.S. Treasury Fiscal Data · ${benchmark.security_desc || 'avg'} · ${benchmark.record_date} · click to refresh`}
                    data-testid="treasury-badge"
                    onClick={async () => {
                      try {
                        const r = await axios.post(`${API}/treasury/poll`);
                        if (r.data?.state?.benchmark) setBenchmark(r.data.state.benchmark);
                      } catch (e) { /* ignore */ }
                    }}
                  >
                    🏛 U.S. Treasury benchmark {Number(benchmark.avg_interest_rate).toFixed(3)}%
                  </span>
                </>
              )}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <div className="mode-toggle" data-testid="mode-toggle">
              <button
                className={mode === "spv_trust" ? "active" : ""}
                onClick={() => switchMode("spv_trust")}
                data-testid="mode-spv-trust"
                title="Bankruptcy-remote SPV formation — UTC § 1013"
              >
                Trust Setup
              </button>
              <button
                className={mode === "discount_sale" ? "active" : ""}
                onClick={() => switchMode("discount_sale")}
                data-testid="mode-discount-sale"
                title="UCC Art. 3 outright sale via Private Negotiable Instrument — permanent transfer"
              >
                Outright Sale
              </button>
              <button
                className={mode === "financing" ? "active" : ""}
                onClick={() => switchMode("financing")}
                data-testid="mode-financing"
                title="UCC Art. 9 secured lending — borrower retains ownership"
              >
                Collateral Loan
              </button>
            </div>

            <button
              className="theme-toggle"
              onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
              data-testid="theme-toggle"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {theme === "dark" ? <Sun size={12} /> : <Moon size={12} />}
              {theme === "dark" ? "Light" : "Dark"}
            </button>

            <button className="btn-ghost" onClick={saveDraft} data-testid="save-draft-btn">
              <Save size={14} />
              {savingState === "saving" ? "Saving…" : savingState === "saved" ? "Saved" : "Save Draft"}
            </button>
            <AuditTrailDrawer
              deal={deal}
              mode={mode}
              open={auditOpen}
              onOpenChange={(v) => { setAuditOpen(v); if (!v) { setAuditIntent(null); setAuditFocusTx(null); } }}
              intent={auditIntent}
              focusTxId={auditFocusTx}
            />
            <button
              className="btn-ghost"
              onClick={() => { setAuditIntent("creditor"); setAuditOpen(true); }}
              data-testid="direct-to-creditor-btn"
              title="Sell / transfer / tender this package to a lender or creditor (debt settlement, swap, finance, refinance, collateral)"
              style={{ color: "#B8860B", borderColor: "#B8860B" }}
            >
              <Building2 size={14} />
              Direct to Creditor
            </button>
            <Link to="/audit" className="btn-ghost" data-testid="audit-dashboard-link" title="Open the production audit dashboard">
              <Layers size={14} />
              Dashboard
            </Link>
            <Link to="/market" className="btn-ghost" data-testid="market-link" title="Secondary market — browse all open inventory">
              <CircleDot size={14} />
              Market
            </Link>
            <Link to="/admin" className="btn-ghost" data-testid="admin-link" title="Admin Back Office — offer inbox & marketplace access tokens">
              <ShieldCheck size={14} />
              Admin
            </Link>
            <button className="btn-ghost" onClick={exportDocx} data-testid="export-docx-btn" title="Download editable Word (.docx) version">
              <FileDown size={14} />
              Export DOCX
            </button>
            <button className="btn-secondary" onClick={() => printDocument("single")} data-testid="print-current-btn">
              <Printer size={14} />
              Export PDF
            </button>
            <button className="btn-primary" onClick={() => printDocument("all")} data-testid="print-all-btn">
              <Download size={14} />
              Download All
            </button>
          </div>
        </div>
      </header>

      {/* ============ MAIN GRID ============ */}
      <div className="no-print grid grid-cols-12 gap-0 min-h-[calc(100vh-58px)]">
        {/* LEFT — Stepper + Form */}
        <aside className="col-span-12 lg:col-span-4 xl:col-span-3 border-r border-[#E5E5DF] bg-white">
          <div className="p-6 border-b border-[#E5E5DF]">
            <div className="eyebrow">Intake</div>
            <h1 className="font-serif-display text-[28px] leading-tight font-medium mt-1">
              Structure the deal
            </h1>
            <p className="text-[13px] text-[#5C5C5C] mt-1 leading-snug">
              Fill each section. Every document updates live.
            </p>
            <DealTemplateLoader deal={deal} setDeal={setDeal} mode={mode} setMode={setMode} />
          </div>

          <div className="grid grid-cols-12">
            <nav className="col-span-5 border-r border-[#E5E5DF] py-3" data-testid="stepper">
              {STEPS.map((s, i) => (
                <div
                  key={s.id}
                  className={`stepper-item ${i === stepIdx ? "active" : ""}`}
                  onClick={() => setStepIdx(i)}
                  data-testid={`step-${s.id}`}
                >
                  <span className="stepper-num">{String(i + 1).padStart(2, "0")}</span>
                  {s.label}
                </div>
              ))}
            </nav>
            <div className="col-span-7 p-6">
              <IntakeForm d={deal} setField={setField} step={currentStep.id} />

              <div className="flex items-center justify-between mt-7 pt-5 border-t border-[#E5E5DF]">
                <button
                  className="btn-ghost"
                  onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
                  disabled={stepIdx === 0}
                  data-testid="step-prev"
                >
                  <ChevronLeft size={14} /> Back
                </button>
                <div className="text-[11px] font-mono-fin text-[#8A8A8A]">
                  {String(stepIdx + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}
                </div>
                <button
                  className="btn-primary"
                  onClick={() => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1))}
                  disabled={stepIdx === STEPS.length - 1}
                  data-testid="step-next"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>

          {/* Drafts */}
          <div className="p-6 border-t border-[#E5E5DF]">
            <div className="flex items-center justify-between">
              <div className="eyebrow">Saved Drafts</div>
              <button className="text-[11px] uppercase tracking-[0.12em] text-[#0B4636] font-bold" onClick={newDeal} data-testid="new-deal-btn">
                + New
              </button>
            </div>
            <div className="mt-3 space-y-2 max-h-56 overflow-auto pr-1" data-testid="drafts-list">
              {drafts.length === 0 && (
                <div className="text-[12px] text-[#8A8A8A]">No drafts yet. Save the current deal to keep working on it later.</div>
              )}
              {drafts.map((dr) => (
                <div
                  key={dr.id}
                  className={`flex items-center justify-between border border-[#E5E5DF] rounded-sm px-3 py-2 cursor-pointer hover:bg-[#FAFAF6] ${activeDraftId === dr.id ? "bg-[#FAFAF6] border-[#0B4636]" : ""}`}
                  onClick={() => loadDraft(dr)}
                  data-testid={`draft-item-${dr.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium truncate">{dr.title}</div>
                    <div className="text-[10px] font-mono-fin text-[#8A8A8A] uppercase tracking-[0.14em]">
                      {PATH_META[dr.mode]?.label || dr.mode} · {new Date(dr.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    className="text-[#9E2A2B] hover:text-[#7a1f20] p-1"
                    onClick={(e) => { e.stopPropagation(); deleteDraft(dr.id); }}
                    data-testid={`delete-draft-${dr.id}`}
                    title="Delete draft"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* RIGHT — Preview */}
        <main className="col-span-12 lg:col-span-8 xl:col-span-9 flex flex-col">
          {/* KPI Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-6 pb-3 bg-white border-b border-[#E5E5DF]">
            <div className="stat-card" data-testid="stat-maturity">
              <div className="stat-label">Maturity Value</div>
              <div className="stat-value">{fmtCurrency(stats.maturity)}</div>
              <div className="stat-sub">{deal.couponRate.toFixed(2)}% · {deal.termDays}d coupon</div>
            </div>
            <div className="stat-card" data-testid="stat-discount">
              <div className="stat-label">Discount @ Sale</div>
              <div className="stat-value">{fmtCurrency(stats.discount)}</div>
              <div className="stat-sub">{stats.discountPct.toFixed(2)}% off maturity</div>
            </div>
            <div className="stat-card" data-testid="stat-yield">
              <div className="stat-label">Buyer Annual Yield</div>
              <div className="stat-value">{stats.annualYield.toFixed(2)}%</div>
              <div className="stat-sub">holding {stats.holdingYield.toFixed(2)}% / {deal.termDays}d</div>
            </div>
            <div className="stat-card" data-testid="stat-spread">
              <div className="stat-label">Financing Spread</div>
              <div className="stat-value">{fmtCurrency(stats.spread)}</div>
              <div className="stat-sub">Maturity − Loan payoff</div>
            </div>
          </div>

          {/* Sensitivity Panel */}
          <div className="px-6 pb-3 bg-white border-b border-[#E5E5DF]">
            <button
              className="btn-ghost"
              onClick={() => setShowSensitivity((v) => !v)}
              data-testid="toggle-sensitivity"
              style={{ marginTop: 4 }}
            >
              <Sliders size={13} />
              {showSensitivity ? "Hide" : "Show"} sensitivity sliders
            </button>
            {showSensitivity && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="sensitivity-panel">
                <SensitivitySlider
                  label="Discount price (% of face)"
                  min={50}
                  max={100}
                  step={0.5}
                  value={deal.faceValue ? (deal.purchasePrice / deal.faceValue) * 100 : 80}
                  display={(v) => `${v.toFixed(1)}%  ·  ${fmtCurrency((v / 100) * deal.faceValue)}`}
                  onChange={(v) => setField("purchasePrice", Math.round((v / 100) * deal.faceValue))}
                  testid="slider-discount-pct"
                />
                <SensitivitySlider
                  label="Coupon rate (% per annum)"
                  min={0}
                  max={15}
                  step={0.25}
                  value={deal.couponRate}
                  display={(v) => `${v.toFixed(2)}%`}
                  onChange={(v) => setField("couponRate", Number(v.toFixed(2)))}
                  testid="slider-coupon"
                />
                <SensitivitySlider
                  label="Term (days to maturity)"
                  min={30}
                  max={720}
                  step={5}
                  value={deal.termDays}
                  display={(v) => `${Math.round(v)} days  ·  ${(v / 30).toFixed(1)} months`}
                  onChange={(v) => setField("termDays", Math.round(v))}
                  testid="slider-term"
                />
                <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-[#E5E5DF]">
                  <div>
                    <label className="field-label">Solve from target yield (% APR)</label>
                    <input
                      type="number"
                      step="0.25"
                      className="field-input"
                      placeholder="e.g. 12.00"
                      data-testid="solve-yield-input"
                      onChange={(e) => {
                        const yld = Number(e.target.value);
                        if (!yld || !deal.faceValue || !deal.termDays) return;
                        // PV = FV / (1 + yld * t/365)
                        const t = deal.termDays / 365;
                        const maturity = deal.faceValue * (1 + (deal.couponRate / 100) * t);
                        const price = maturity / (1 + (yld / 100) * t);
                        setField("purchasePrice", Math.round(price));
                      }}
                    />
                  </div>
                  <div className="stat-card" style={{ padding: "8px 12px" }}>
                    <div className="stat-label">Implied IRR</div>
                    <div className="stat-value" style={{ fontSize: "18px" }}>{stats.annualYield.toFixed(2)}%</div>
                  </div>
                  <div className="stat-card" style={{ padding: "8px 12px" }}>
                    <div className="stat-label">Purchase Price</div>
                    <div className="stat-value" style={{ fontSize: "18px" }}>{fmtCurrency(deal.purchasePrice, { cents: false })}</div>
                  </div>
                  <div className="stat-card" style={{ padding: "8px 12px" }}>
                    <div className="stat-label">Discount of Face</div>
                    <div className="stat-value" style={{ fontSize: "18px" }}>{(100 - (deal.purchasePrice / deal.faceValue) * 100).toFixed(2)}%</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Path Explainer Banner */}
          <div className="px-6 py-3 bg-white border-b border-[#E5E5DF]" data-testid="path-explainer">
            <div className="flex items-start gap-4">
              <div className="flex flex-col items-center pt-1">
                <div className="w-1.5 h-1.5 rounded-full bg-[#0B4636]" />
                <div className="w-px h-5 bg-[#D1D1C7] my-1" />
                <div className="w-1.5 h-1.5 rounded-full bg-[#D1D1C7]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <span className="eyebrow">{mode === "spv_trust" ? "Path 0" : mode === "discount_sale" ? "Path 1" : "Path 2"}</span>
                  <span className="font-serif-display text-[17px] font-medium leading-none">{PATH_META[mode].label}</span>
                  <span className="text-[10px] font-mono-fin uppercase tracking-[0.16em] text-[#0B4636] bg-[#E6EFEC] px-2 py-0.5 rounded-sm">
                    {PATH_META[mode].subtitle}
                  </span>
                </div>
                <p className="text-[12.5px] text-[#5C5C5C] leading-snug">
                  {PATH_META[mode].blurb} <span className="text-[#0A0A0A] font-medium">{PATH_META[mode].counterparty(deal)}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 px-6 py-4 bg-[#F5F5F0] border-b border-[#E5E5DF] overflow-x-auto" data-testid="doc-tabs">
            <div className="flex items-center gap-2 mr-2 text-[#5C5C5C]">
              <Layers size={14} />
              <span className="eyebrow">Documents</span>
            </div>
            {docsList.map((doc) => (
              <button
                key={doc.id}
                className={`tab-pill ${activeDoc === doc.id ? "active" : ""}`}
                onClick={() => setActiveDoc(doc.id)}
                data-testid={`tab-${doc.id}`}
              >
                {activeDoc === doc.id && <CircleDot size={11} className="inline mr-1 -mt-0.5" />}
                {doc.label}
              </button>
            ))}
          </div>

          {/* Paper Canvas */}
          <div className="paper-canvas flex-1 overflow-auto" data-testid="paper-canvas">
            <ActiveDoc d={deal} />
          </div>
        </main>
      </div>

      {/* ============ HIDDEN PRINT ROOT — used for window.print() ============ */}
      <div
        className="print-root"
        style={{ display: printMode ? "block" : "none" }}
      >
        {printMode === "all"
          ? allDocsForPrint.map(({ id, component: C }) => <C key={id} d={deal} />)
          : <ActiveDoc d={deal} />}
      </div>
    </div>
  );
}

export default App;
